'use client';

import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { deleteUser } from '../clients/userClient';
import BasicModal from '../components/BasicModal';
import { LoadingIcon } from '../components/LoadingIcon';
import Sidebar from '../components/Sidebar';
import UpgradeModal from '../components/UpgradeModal';
import { useFeatureGate } from '../context/FeatureGate/FeatureGateContext';
import { useGlobalContext } from '../context/GlobalProvider';
import { useUserContext } from '../context/UserProvider';

const renderDeleteConfirmationBody = (
  deleteConfirmationText: string,
  setDeleteConfirmationText: (text: string) => void,
  deleteConfirmationEmail: string,
  setDeleteConfirmationEmail: (email: string) => void,
  handleDeleteAccount: () => void,
  isDeleting: boolean,
  setIsDeleteConfirmationOpen: (isOpen: boolean) => void,
  isAccountDeletionEnabled: boolean,
  doesEmailMatch: boolean,
  doesTextMatch: boolean
) => (
  <>
    <p className="my-4">
      Deleting your account will permanently remove all your data, including meeting notes and
      templates. <b>This action cannot be undone.</b>
    </p>
    <p>You will be logged out and your account and data will be permanently deleted.</p>

    {/* Make user type their email address to confirm */}
    <div className="relative w-full my-4">
      <input
        type="email"
        value={deleteConfirmationEmail}
        onChange={(e) => setDeleteConfirmationEmail(e.target.value)}
        className="w-full p-2 sm:text-md sm:leading-6 text-gray-600 focus:text-gray-900 border-b outline-none border-gray-200 focus:border-indigo-600 placeholder:text-gray-400 focus:placeholder:text-gray-500 pr-8"
        placeholder="Type the email address associated with this account"
      />
      {doesEmailMatch && (
        <CheckCircleIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-teal-500 h-5 w-5" />
      )}
    </div>

    {/* Make user type "DELETE to confirm" */}
    <div className="relative w-full mb-4">
      <input
        type="text"
        value={deleteConfirmationText}
        onChange={(e) => setDeleteConfirmationText(e.target.value)}
        className="w-full p-2 sm:text-md sm:leading-6 text-gray-600 focus:text-gray-900 border-b outline-none border-gray-200 focus:border-indigo-600 placeholder:text-gray-400 focus:placeholder:text-gray-500 pr-8"
        placeholder='Type "DELETE" to confirm'
      />
      {doesTextMatch && (
        <CheckCircleIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-teal-500 h-5 w-5" />
      )}
    </div>

    <div className="flex justify-end space-x-2 mt-8">
      <button
        onClick={handleDeleteAccount}
        // className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-300 "
        className="text-base font-medium rounded text-white bg-red-700 hover:bg-red-500 px-6 py-1 text-indigo-800 hover:text-white transition-color duration-300 disabled:bg-gray-200"
        disabled={!isAccountDeletionEnabled}
      >
        {isDeleting ? (
          <div className="flex items-center gap-2">
            <p className="text-gray-600">Deleting...</p>
            <LoadingIcon />
          </div>
        ) : (
          <p>Delete Account</p>
        )}
      </button>

      {/* Hide cancel button if delete mutation is already triggered */}
      {!isDeleting && (
        <button
          onClick={() => setIsDeleteConfirmationOpen(false)}
          disabled={isDeleting}
          className="text-base font-medium rounded bg-indigo-50 hover:bg-indigo-400 px-6 py-1 text-indigo-800 hover:text-white transition-color duration-300"
        >
          Cancel
        </button>
      )}
    </div>
  </>
);

export default function SettingsPage() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { name: userName, email: userEmail } = useUserContext();
  const { featureGate } = useFeatureGate();
  const shouldShowUpgradeButton = featureGate && !featureGate.isPaidTier();

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteConfirmationEmail, setDeleteConfirmationEmail] = useState('');

  const { setState } = useGlobalContext();

  const deleteUserMutation = useMutation({
    mutationFn: () => {
      return deleteUser();
    },
    onSuccess: async () => {
      // Log user out and redirect to account deleted page
      await signOut();
      navigate('/');
    },
    onError: () => {
      // Display toast
      setState((prev) => ({ ...prev, deleteAccountError: true }));
    }
  });

  const doesEmailMatch = deleteConfirmationEmail === userEmail;
  const doesTextMatch = deleteConfirmationText === 'DELETE';

  const isAccountDeletionEnabled = doesTextMatch && doesEmailMatch && !deleteUserMutation.isPending;

  const handleDeleteAccount = () => {
    if (isAccountDeletionEnabled) {
      deleteUserMutation.mutate();
    } else {
      alert('Please make sure you have correctly typed your email and the word "DELETE.');
    }
  };

  return (
    <div>
      <Sidebar currentPageName={''} />
      <main className="lg:ml-72">
        <div className="relative w-full">
          {/* Upgrade Button*/}
          {shouldShowUpgradeButton && (
            <button
              onClick={() => setIsUpgradeModalOpen(true)}
              className="absolute flex items-center top-6 right-6 z-50 
                 hover:bg-teal-400 text-sm font-semibold text-white
                 px-3.5 py-2 rounded-lg shadow-lg bg-teal-500
                 transition duration-300"
            >
              <p>Upgrade</p>
            </button>
          )}
        </div>
        <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6 mb-12">
          {/* Heading */}
          <div className="border-b border-gray-200 pt-4 pb-6 text-gray-800">
            <h3 className="text-3xl font-bold">Your Account</h3>
          </div>

          <div className="mx-auto mt-4 max-w-7xl space-y-16 sm:space-y-20 lg:mx-0 p-4 lg:p-8">
            <div>
              <h2 className="text-xl/7 font-semibold text-gray-900">User Details</h2>
              {/* <p className="mt-1 text-sm/6 text-gray-500">
                This information will be displayed publicly so be careful what you share.
              </p> */}

              <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-base/6">
                <div className="py-6 sm:flex">
                  <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                    Full name
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">{userName}</div>
                    {/* <button
                      type="button"
                      className="font-semibold text-indigo-600 hover:text-indigo-500 transition duration-300"
                    >
                      Update
                    </button> */}
                  </dd>
                </div>
                <div className="py-6 sm:flex">
                  <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                    Email address
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">{userEmail}</div>
                    {/* <button
                      type="button"
                      className="font-semibold text-indigo-600 hover:text-indigo-500"
                    >
                      Update
                    </button> */}
                  </dd>
                </div>
                {/* <div className="py-6 sm:flex">
                  <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                    Password
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">***************</div> */}
                {/* <button
                      type="button"
                      className="font-semibold text-indigo-600 hover:text-indigo-500 transition duration-300"
                    >
                      Update
                    </button> */}
                {/* </dd>
                </div> */}
              </dl>
            </div>

            <div>
              <h2 className="text-xl/7 font-semibold text-gray-900">Account Settings</h2>
              {/* <p className="mt-1 text-sm/6 text-gray-500">Connect bank accounts to your account.</p> */}

              <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-base/6">
                <div className="py-6 sm:flex">
                  <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                    Subscription Plan
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">{featureGate?.tier}</div>

                    {!featureGate?.isPaidTier() && (
                      <button
                        onClick={() => setIsUpgradeModalOpen(true)}
                        type="button"
                        className="font-semibold text-indigo-600 hover:text-indigo-500 transition duration-300"
                      >
                        Upgrade
                      </button>
                      // )
                      // : (
                      //   <button
                      //     onClick={() => {}}
                      //     type="button"
                      //     className="font-semibold text-indigo-600 hover:text-indigo-500 transition duration-300"
                      //   >
                      //     Cancel
                      //   </button>
                    )}
                  </dd>
                </div>
              </dl>

              <div className="flex border-t border-gray-100 pt-6">
                <button
                  type="button"
                  onClick={() => setIsDeleteConfirmationOpen(true)}
                  className="text-base/6 font-semibold text-red-600 hover:text-red-800 transition duration-300"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <BasicModal
        isOpen={isDeleteConfirmationOpen}
        setIsOpen={setIsDeleteConfirmationOpen}
        title="Delete Account"
        children={renderDeleteConfirmationBody(
          deleteConfirmationText,
          setDeleteConfirmationText,
          deleteConfirmationEmail,
          setDeleteConfirmationEmail,
          handleDeleteAccount,
          deleteUserMutation.isPending,
          setIsDeleteConfirmationOpen,
          isAccountDeletionEnabled,
          doesEmailMatch,
          doesTextMatch
        )}
      />

      <UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} />
    </div>
  );
}
